
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

// Simple UK postcode validation (loose)
const isValidPostcode = pc => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(pc.trim());

// Money formatter with graceful fallback
const money = v =>
  v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(2);

const scrollToBreakdown = () => {
  const el = document.getElementById("parking-breakdown");
  if (el) {
    el.setAttribute("open", "true");
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export default function ParkingVsTaxi() {
  const MARKET_FLAGS = {
    heathrow_official: false,
    edisonlee: false,
    minicab: false,
    online_first: false,
    online_second: false,
    uber_reserve: false
  };
  const [form, setForm] = useState({
    pickup_postcode: "",
    dropoff_terminal: "LHR5",
    passengers: 1,
    luggage: 0,
    days_parked: 5,
    pickup_datetime: ""
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);

  // Prefill defaults
  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay(); // 0=Sun
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = n => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm(prev => ({
      ...prev,
      pickup_postcode: "KT14 6AA",
      dropoff_terminal: "LHR5",
      passengers: 4,
      luggage: 3,
      days_parked: 5,
      pickup_datetime: nextMondayAt11()
    }));
  }, []);

  const submit = async () => {
    if (!form.pickup_postcode.trim() || !isValidPostcode(form.pickup_postcode)) {
      setError("Please enter a valid UK pickup postcode.");
      return;
    }
    if (!form.pickup_datetime) {
      setError("Please select a pickup date and time.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...form,
        passengers: Math.min(4, Math.max(1, Number(form.passengers) || 1)),
        luggage: Math.min(3, Math.max(0, Number(form.luggage) || 0))
      };
      const res = await api.post("/api/parking/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
  };

  // Auto-run once prefill is ready
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.pickup_datetime]);

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Heathrow Parking vs Taxi Cost Comparison 2026: Which is Cheaper?</title>

        <meta
          name="description"
          content="Updated Jan 2026. Compare real Heathrow parking prices vs taxi fares. Includes fuel costs, the £7 drop-off fee, and Hassle Scores for all terminals. Save up to £800+ on long-distance trips."
        />

        <meta
          name="keywords"
          content="Heathrow parking vs taxi, Heathrow parking cost T2, Heathrow parking cost T3, Heathrow parking cost T4, Heathrow parking cost T5, cheaper to park or taxi Heathrow, Heathrow taxi fare estimator, Heathrow terminal 2 taxi time, Heathrow terminal 5 parking price"
        />

        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/heathrow-parking-vs-taxi"
        />

        <meta property="og:title" content="Heathrow Parking vs Taxi Comparison" />
        <meta
          property="og:description"
          content="Compare Heathrow parking prices vs taxi transfers and see which option saves you more money, time and hassle for terminals 2, 3, 4, and 5."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/heathrow-parking-vs-taxi"
        />

        {/* ✅ JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Parking vs Taxi Comparison",
            url: "https://www.cheapcabgrab.com/heathrow-parking-vs-taxi",
            description:
              "Compare Heathrow Airport parking prices vs taxi transfers using live parking data and real taxi fares.",
            about: [
              {
                "@type": "Service",
                name: "Heathrow Taxi",
                url: "https://www.cheapcabgrab.com/heathrow-taxi"
              },
              {
                "@type": "Service",
                name: "Heathrow Terminal Finder",
                url: "https://www.cheapcabgrab.com/terminalFinder"
              },
              {
                "@type": "Service",
                name: "Flight Checker",
                url: "https://www.cheapcabgrab.com/flight-checker"
              }
            ],
            mainEntity: [
              {
                "@type": "Question",
                name: "Is it cheaper to park at Heathrow or take a taxi?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Use this calculator to compare Heathrow parking costs vs two taxi trips. We show which is cheaper and the saving versus the next best option."
                }
              },
              {
                "@type": "Question",
                name: "What are parking costs at Heathrow Terminal 2/3/4/5?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We include official Heathrow parking prices for T2, T3, T4 and T5, plus fuel/time estimates for driving and returning."
                }
              },
              {
                "@type": "Question",
                name: "How long will a taxi take to Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We use OSRM road distance and drive-time estimates to show typical minutes to each terminal along with fatigue/family/convenience indicators."
                }
              },
              {
                "@type": "Question",
                name: "Do you include ride-hail and black cab ranges?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. We show fixed CheapCabGrab fares plus modelled ranges for ride hailing and black cabs, and when available, real market benchmarks."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Heathrow Parking vs Taxi Comparison Tool",
            operatingSystem: "All",
            applicationCategory: "TravelApplication",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "128"
            }
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      {/* ================= HEADER ================= */}
      <div className="parking-header">
        <h1>🚗 Heathrow Parking vs Taxi</h1>
        <p>
          Compare the real cost of airport parking (including fuel and hassle)
          versus taking two taxi journeys with CheapCabGrab.
        </p>
      </div>

    <div className="parking-form">
      <h2>Your Journey</h2>

      <div className="form-group">
        <label>Pickup Postcode</label>
        <input
          className="parking-input"
          value={form.pickup_postcode}
          onChange={(e) =>
            setForm({ ...form, pickup_postcode: e.target.value })
          }
        />
      </div>

      <div className="form-group">
        <label>Heathrow Terminal</label>
        <select
          className="parking-input"
          value={form.dropoff_terminal}
          onChange={(e) =>
            setForm({ ...form, dropoff_terminal: e.target.value })
          }
        >
          <option value="LHR2">Terminal 2</option>
          <option value="LHR3">Terminal 3</option>
          <option value="LHR4">Terminal 4</option>
          <option value="LHR5">Terminal 5</option>
        </select>
      </div>

      <div className="form-group">
        <label>Passengers</label>
        <input
          type="number"
          min="1"
          max="4"
          step="1"
          className="parking-input"
          value={form.passengers}
          onChange={(e) =>
            setForm({
              ...form,
              passengers:
                e.target.value === ""
                  ? ""
                  : Math.min(4, Math.max(1, Number(e.target.value)))
            })
          }
          onBlur={() => {
            const num = Number(form.passengers);
            if (Number.isNaN(num)) {
              setForm({ ...form, passengers: 1 });
            } else {
              setForm({ ...form, passengers: Math.min(4, Math.max(1, num)) });
            }
          }}
        />
      </div>

      <div className="form-group">
        <label>Luggage</label>
        <input
          type="number"
          min="0"
          max="3"
          step="1"
          className="parking-input"
          value={form.luggage}
          onChange={(e) =>
            setForm({
              ...form,
              luggage:
                e.target.value === ""
                  ? ""
                  : Math.min(3, Math.max(0, Number(e.target.value)))
            })
          }
          onBlur={() => {
            const num = Number(form.luggage);
            if (Number.isNaN(num)) {
              setForm({ ...form, luggage: 0 });
            } else {
              setForm({ ...form, luggage: Math.min(3, Math.max(0, num)) });
            }
          }}
        />
      </div>

      <div className="form-group">
        <label>Days Parked</label>
        <input
          type="number"
          min="1"
          className="parking-input"
          value={form.days_parked}
          onChange={(e) =>
            setForm({ ...form, days_parked: Number(e.target.value) })
          }
        />
      </div>

      <div className="form-group">
        <label>Pickup Date & Time</label>
        <input
          type="datetime-local"
          className="parking-input"
          value={form.pickup_datetime ? form.pickup_datetime.slice(0,16) : ""}
          onChange={e => {
            const local = e.target.value;
            setForm({ ...form, pickup_datetime: local ? new Date(local).toISOString() : "" });
          }}
        />
      </div>

      <button className="parking-btn" onClick={submit}>
        {loading ? "Calculating…" : "Compare Now"}
      </button>

      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>

    {result && (() => {
      const bestMap = {
        CHEAPCABGRAB: "🚖 CheapCabGrab Taxi",
        RIDE_HAIL: "📱 Ride Hailing (typical)",
        BLACK_CAB: "🚕 Black Cab",
        PARKING: "🚗 Heathrow Parking"
      };
      const bestKey = result?.recommendation?.best_value;
      const bestLabel = bestMap[bestKey] || "—";
      const badge =
        bestKey === "PARKING"
          ? { text: "Value King", color: "#16a34a", bg: "#dcfce7" }
          : bestKey
          ? { text: "VIP Choice", color: "#ca8a04", bg: "#fef9c3" }
          : null;

      // Derived values for headline and ranges
      const parkingTotal = result?.costs?.parking?.total ?? null;
      const parkingPrice = result?.costs?.parking?.parking_price ?? null;
      const fuelPrice = result?.costs?.parking?.fuel_price ?? null;
      const timeCost =
        parkingTotal != null && parkingPrice != null && fuelPrice != null
          ? Number((parkingTotal - parkingPrice - fuelPrice).toFixed(2))
          : null;

      const ccgTwoWay = result?.costs?.taxi?.cheapcabgrab_two_way ?? null;
      const delta =
        parkingTotal != null && ccgTwoWay != null
          ? Number((parkingTotal - ccgTwoWay).toFixed(2))
          : null;

      let headlineLine = "";
      if (delta == null) {
        headlineLine = "See breakdown below.";
      } else if (Math.abs(delta) < 5) {
        headlineLine = `Costs are very similar (within £${money(Math.abs(delta))}).`;
      } else if (delta > 0) {
        headlineLine = `Taxi is cheaper by £${money(delta)} vs parking.`;
      } else {
        headlineLine = `Parking is cheaper by £${money(Math.abs(delta))} vs 2 taxi trips.`;
      }

      const benchMatch = result?.costs?.taxi?.benchmarks?.match;
      const noBench = benchMatch === "NONE";
      const distanceKm = result?.journey?.distance_km;
      const longDistance = distanceKm != null && Number(distanceKm) > 130; // ~80 miles
      const distSource = result?.journey?.distance_source;
      let confidence = "Low";
      if (distSource === "OSRM" && benchMatch === "EXACT") confidence = "High";
      else if (distSource === "OSRM" && benchMatch === "OUTWARD") confidence = "Medium";
      else if (distSource === "OSRM") confidence = "Medium";

      const bcMid = result?.costs?.taxi?.black_cab_two_way ?? null;


      const blackCabRange =
        result?.costs?.taxi?.black_cab_two_way_range ??
        (result?.costs?.taxi?.black_cab_two_way != null
          ? {
              min: Number((result.costs.taxi.black_cab_two_way * 0.98).toFixed(2)),
              mid: Number((result.costs.taxi.black_cab_two_way).toFixed(2)),
              max: Number((result.costs.taxi.black_cab_two_way * 1.02).toFixed(2)),
            }
          : null);




  {/*      const blackCabRange =
        bcMid != null
          ? {
              min: Number((bcMid * 0.9).toFixed(2)),
              mid: Number(bcMid.toFixed(2)),
              max: Number((bcMid * 1.15).toFixed(2))
            }
          : null; */}


const blackCabNote = result?.costs?.taxi?.black_cab_note ?? null;


      return (
        <div className="parking-results">
          {/* =================== TOP SUMMARY CARD =================== */}
          <div className="best-choice">
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>✅ Best Choice: {bestLabel}</span>
              {badge && (
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: 999,
                    background: badge.bg,
                    color: badge.color,
                    fontWeight: 700,
                    fontSize: 12
                  }}
                >
                  {badge.text}
                </span>
              )}
            </div>
            <span style={{ fontWeight: 700 }}>{headlineLine}</span>

            {bestKey === "RIDE_HAIL" && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                Note: ride-hailing can surge, so final price may be higher.
              </div>
            )}
            <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
              Confidence: <strong>{confidence}</strong> — based on road distance & time ({distSource || "—"}) and quote benchmarks ({benchMatch || "NONE"}).
            </div>
            {noBench && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                Confidence is lower because we don’t yet have a recent market quote for this postcode.
              </div>
            )}
            {longDistance && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                For long-distance travel, pre-booked transfers often beat ride-hailing.
              </div>
            )}
            {timeCost != null && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                Parking total includes fuel plus ~£{money(timeCost)} for time/convenience.
              </div>
            )}
            {bestKey === "PARKING" && (
              <div style={{ fontSize: 12, color: "#374151", marginTop: 8 }}>
                Book official Heathrow parking:{" "}
                <a
                  href="https://www.heathrow.com/transport-and-directions/heathrow-parking"
                  target="_blank"
                  rel="noreferrer"
                >
                  Heathrow Airport parking providers
                </a>{" "}
                (recommended for availability and security).
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="parking-btn"
                style={{ width: "auto", padding: "8px 12px", borderRadius: 10 }}
                onClick={scrollToBreakdown}
              >
                View breakdown ↓
              </button>
            </div>
          </div>

          {/* =================== COLLAPSABLE 1: PRICE COMPARISON =================== */}
          <details className="parking-accordion" id="parking-breakdown" style={{ marginTop: 14 }} open>
            <summary className="parking-accordion-summary">
              <span>💷 Price comparison (parking vs taxi options)</span>
            </summary>

            <div className="responsive-table" style={{ marginTop: 10 }}>
              <table className="parking-table">
                <thead>
                  <tr>
                    <th>Option</th>
                    <th>Cost (£)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2} style={{ background: "#f1f5f9", fontWeight: 800, color: "#0f172a" }}>
                      Parking options
                    </td>
                  </tr>
                  <tr>
                    <td>🚗 Heathrow Parking (total)</td>
                    <td>{money(result.costs.parking.total)}</td>
                  </tr>

                  <tr>
                    <td style={{ paddingLeft: 28, color: "#666" }}>
                      └ Parking price ({result.costs.parking.pricing_mode})
                    </td>
                    <td style={{ color: "#666" }}>{money(result.costs.parking.parking_price)}</td>
                  </tr>

                  <tr>
                    <td style={{ paddingLeft: 28, color: "#666" }}>
                      └ Fuel estimate (return driving)
                      <div className="line-wrap" style={{ fontSize: 11, color: "#6b7280", marginTop: 2, whiteSpace: "normal" }}>
                        Based on average UK fuel prices (£1.35–£1.50 per litre) and real road distance (OSRM).
                        Prices vary daily; this uses a conservative mid-range.
                      </div>
                    </td>
                    <td style={{ color: "#666" }}>{money(result.costs.parking.fuel_price)}</td>
                  </tr>

                  <tr>
                    <td style={{ paddingLeft: 28, color: "#666" }}>└ Time & convenience cost</td>
                    <td style={{ color: "#666" }}>{timeCost == null ? "—" : money(timeCost)}</td>
                  </tr>

                  <tr>
                    <td colSpan={2} style={{ background: "#f8fafc", fontWeight: 800, color: "#0f172a" }}>
                      Taxi options
                    </td>
                  </tr>
                  <tr className="highlight-row">
                    <td>🚖 CheapCabGrab (2 trips)</td>
                    <td>{money(result.costs.taxi.cheapcabgrab_two_way)}</td>
                  </tr>

                  <tr>
                    <td>📱 Ride Hailing (range)</td>
                    <td>
                      {result.costs.taxi.ride_hail_two_way
                        ? `£${money(result.costs.taxi.ride_hail_two_way.min)} – £${money(result.costs.taxi.ride_hail_two_way.max)}`
                        : "—"}
                      {result.costs.taxi.ride_hail_two_way?.mid != null && (
                        <div style={{ fontSize: 12, color: "#666" }}>
                          typical ~£{money(result.costs.taxi.ride_hail_two_way.mid)}
                        </div>
                      )}
                    </td>
                  </tr>

                  <tr>
                    <td>🚕 Black Cab (range)</td>
                    <td>
                      {blackCabRange
                        ? `£${money(blackCabRange.min)} – £${money(blackCabRange.max)}`
                        : "—"}
                      {blackCabRange?.mid != null && (
                        <div style={{ fontSize: 12, color: "#666" }}>
                          typical ~£{money(blackCabRange.mid)}
                        </div>
                      )}
                      {blackCabNote && (
                        <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                          {blackCabNote}
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          {/* =================== COLLAPSABLE 2: TRIP DETAILS =================== */}
          <details className="parking-accordion" style={{ marginTop: 14 }}>
            <summary className="parking-accordion-summary">
              <span>📍 Trip details (distance, time, personalisation)</span>
            </summary>

            <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>
              <div>
                <strong>Pickup:</strong> {result.journey.pickup_postcode}{" "}
                {result.journey?.town ? `(${result.journey.town})` : ""}
              </div>
              <div>
                <strong>Terminal:</strong> {result.costs?.parking?.terminal || result.journey.dropoff_terminal}
              </div>
              <div>
                <strong>When:</strong>{" "}
                {result.journey.pickup_datetime
                  ? new Date(result.journey.pickup_datetime).toLocaleString()
                  : "—"}
              </div>
              <div>
                <strong>Passengers:</strong> {result.journey.passengers} &nbsp;|&nbsp;{" "}
                <strong>Luggage:</strong> {result.journey.luggage}
              </div>
              <div>
                <strong>Days parked:</strong> {result.journey.days_parked}
              </div>

              <hr style={{ margin: "12px 0" }} />

              <div>
                <strong>Road distance:</strong> {money(result.journey.distance_km)} km{" "}
                <span style={{ fontSize: 12, color: "#666" }}>({result.journey.distance_source || "—"})</span>
              </div>
              <div>
                <strong>Typical drive time:</strong>{" "}
                {result.journey.duration_min != null ? `${result.journey.duration_min} min` : "—"}
              </div>
              <div>
                <strong>Crow distance:</strong>{" "}
                {result.journey.crow_km != null ? `${money(result.journey.crow_km)} km` : "—"}
              </div>
            </div>
          </details>

          {/* =================== COLLAPSABLE 3: REAL QUOTE BENCHMARKS =================== */}
          <details className="parking-accordion" style={{ marginTop: 14 }}>
            <summary className="parking-accordion-summary">
              <span>📊 Real quote benchmarks (recent market prices)</span>
            </summary>

            <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>
              {(() => {
                const bandAbsOrPct = (mid, { abs = 1, pct = 0.08, threshold = 120 } = {}) => {
                  if (mid == null || Number.isNaN(Number(mid))) return null;
                  const m = Number(mid);
                  if (m <= threshold) {
                    return { min: Number((m - abs).toFixed(2)), mid: Number(m.toFixed(2)), max: Number((m + abs).toFixed(2)) };
                  }
                  return {
                    min: Number((m * (1 - pct)).toFixed(2)),
                    mid: Number(m.toFixed(2)),
                    max: Number((m * (1 + pct)).toFixed(2))
                  };
                };

                const marketExtras = (() => {
                  const m = result.costs?.taxi?.market;
                  if (!m) return [];
                  const entries = [
                    { key: "heathrow_official", label: "Heathrow official (two-way, saloon)", val: m.heathrow_official?.saloon },
                    { key: "edisonlee", label: "Edison Lee (two-way, saloon)", val: m.edisonlee?.saloon },
                    { key: "minicab", label: "Minicab (two-way, saloon)", val: m.minicab?.saloon },
                    { key: "online_first", label: "Online taxi 1st (two-way, saloon)", val: m.online_taxi_first?.mpv },
                    { key: "online_second", label: "Online taxi 2nd (two-way, saloon)", val: m.online_taxi_second?.mpv },
                    { key: "uber_reserve", label: "Uber Reserve (two-way, saloon)", val: m.uber_reserve?.saloon }
                  ];
                  return entries
                    .filter(e => MARKET_FLAGS[e.key])
                    .map(e => ({ ...e, range: bandAbsOrPct(e.val ? e.val * 2 : null) }))
                    .filter(e => e.range);
                })();

                const airportTwoWay = result.costs?.taxi?.benchmarks?.airport_taxi?.saloon
                  ? {
                      min: Number((result.costs.taxi.benchmarks.airport_taxi.saloon.min * 2).toFixed(2)),
                      max: Number((result.costs.taxi.benchmarks.airport_taxi.saloon.max * 2).toFixed(2))
                    }
                  : null;

                const onlineTwoWay = result.costs?.taxi?.benchmarks?.online_taxi?.saloon
                  ? {
                      min: Number((result.costs.taxi.benchmarks.online_taxi.saloon.min * 2).toFixed(2)),
                      max: Number((result.costs.taxi.benchmarks.online_taxi.saloon.max * 2).toFixed(2))
                    }
                  : null;

                return result.costs?.taxi?.benchmarks?.match && result.costs.taxi.benchmarks.match !== "NONE" ? (
                  <>
                    <div>
                      <strong>Match:</strong> {result.costs.taxi.benchmarks.match}
                      &nbsp;|&nbsp;<strong>Town:</strong> {result.costs.taxi.benchmarks.town || "—"}
                      &nbsp;|&nbsp;<strong>Captured:</strong>{" "}
                      {result.costs.taxi.benchmarks.captured_at
                        ? new Date(result.costs.taxi.benchmarks.captured_at).toLocaleString()
                        : "—"}
                    </div>

                    <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                      Benchmarks are shown as <strong>two-way</strong> references for context.
                    </div>

                    <hr style={{ margin: "12px 0" }} />

                    <div>
                      <strong>Airport taxi (saloon, return):</strong>{" "}
                      {airportTwoWay
                        ? `£${airportTwoWay.min} – £${airportTwoWay.max}`
                        : "—"}
                    </div>

                    <div>
                      <strong>Online taxi (saloon, return):</strong>{" "}
                      {onlineTwoWay
                        ? `£${onlineTwoWay.min} – £${onlineTwoWay.max}`
                        : "—"}
                    </div>

                    {marketExtras.length > 0 && (
                      <>
                        <hr style={{ margin: "12px 0" }} />
                        {marketExtras.map((e, idx) => (
                          <div key={idx}>
                            <strong>{e.label}:</strong> £{e.range.min} – £{e.range.max}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <div style={{ color: "#666" }}>
                    No recent benchmark quote found for this postcode yet. Estimates above still use real road distance.
                  </div>
                );
              })()}
            </div>
          </details>

          {/* =================== COLLAPSABLE 4: EFFORT INDICATORS =================== */}
          <details className="parking-accordion" style={{ marginTop: 14 }}>
            <summary className="parking-accordion-summary">
              <span>🧠 Travel effort indicators (fatigue, family, convenience)</span>
            </summary>

            <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>
              <div><strong>Fatigue:</strong> {result.scores?.fatigue ?? "—"}</div>
              <div><strong>Family impact:</strong> {result.scores?.family ?? "—"}</div>
              <div><strong>Convenience:</strong> {result.scores?.convenience ?? "—"}</div>

              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Indicators factor in unloading luggage (family impact), concentration levels (fatigue), and transfer bus/walk time (convenience).
              </div>
            </div>
          </details>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
            <strong>How we calculate your Hassle Score</strong>
            <p style={{ marginTop: 6, marginBottom: 6, fontSize: 14, color: "#374151" }}>
              We combine three factors: unloading luggage (family impact), concentration needed for driving (fatigue), and transfer/waiting time (convenience). This gives a clearer picture than price alone.
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
              <li>Real road distance and time (OSRM) per terminal.</li>
              <li>£7 drop-off fee and fuel costs included.</li>
              <li>Short Stay vs Park & Ride vs taxis compared for safety and cost.</li>
            </ul>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fffaf0" }}>
            <strong>💡 Pro Tip</strong>
            <p style={{ marginTop: 6, marginBottom: 0, fontSize: 14, color: "#374151" }}>
              For travellers from Manchester (M1), parking usually wins by £800+ on week-long trips. For nearby postcodes (SL, TW, UB), taxis often beat Short Stay once the £7 drop-off fee is included.
            </p>
          </div>

          <details className="parking-accordion" style={{ marginTop: 14 }}>
            <summary className="parking-accordion-summary">
              <span>✅ Why this Heathrow Parking vs Taxi Calculator is Reliable</span>
            </summary>

            <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: "#374151" }}>
              This comparison uses real-world data, not assumptions.
              <ul style={{ marginTop: 8, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Road distance and drive time are calculated using OSRM (OpenStreetMap routing).</li>
                <li>Parking prices are based on cached official Heathrow Airport rates.</li>
                <li>Fuel costs are estimated using average UK petrol prices and real return distance.</li>
                <li>Taxi fares use live distance-based pricing rules and recent market benchmarks.</li>
                <li>Where recent quotes are unavailable, confidence levels are shown clearly.</li>
              </ul>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                This tool is designed to help you decide — not push a single option.
              </div>
            </div>
          </details>

          {/* =================== COLLAPSABLE 5: WHAT NEXT / LINKS =================== */}
          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>Plan your Heathrow journey:</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
              <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
              <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
              <li>✈️ <a href="/flight-checker">Check your flight</a></li>
              <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
              <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
              <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
            </ul>
          </div>

          <p className="parking-disclaimer" style={{ marginTop: 14 }}>
            {result.disclaimer}
          </p>

          {bestKey === "PARKING" && (
            <div style={{ marginTop: 10, fontSize: 13, color: "#374151" }}>
              <strong>Book official Heathrow parking:</strong>{" "}
              <a
                href="https://www.heathrow.com/transport-and-directions/heathrow-parking"
                target="_blank"
                rel="noreferrer"
              >
                Heathrow Airport parking providers
              </a>{" "}
              (recommended for availability and security).
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            Last updated: 12 Jan 2026
          </div>

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button
              className="parking-btn"
              onClick={() => (window.location.href = "/")}
            >
              🚖 Book Your CheapCabGrab Taxi
            </button>
          </div>
        </div>
      );
    })()}

    <div style={{ marginTop: "50px" }}>
      <ReviewsBar />
    </div>
  </div>
  );
}
